import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import AssigneeSelector from './AssigneeSelector';
import TeamSelector from './TeamSelector';
import { useRoleNamesForId } from '../../hooks/useRoles';
import type { PatientConversationActions } from '../../hooks/usePatientConversationActions';

/** `bar` = compact strip above the patient Chat tab, `panel` = wider slide-over header. */
export type ConversationActionsVariant = 'bar' | 'panel';

interface ConversationActionsProps {
    actions: PatientConversationActions;
    variant: ConversationActionsVariant;
}

const READ_STATE_STYLES: Record<
    ConversationActionsVariant,
    {
        markRead: string;
        markReadIcon: string;
        markReadSpinner: string;
        reviewedGroup: string;
        reviewedPill: string;
        markUnread: string;
    }
> = {
    bar: {
        markRead:
            'flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0',
        markReadIcon: 'w-3.5 h-3.5',
        markReadSpinner:
            'h-3.5 w-3.5 rounded-full border-2 border-green-400 border-t-transparent animate-spin',
        reviewedGroup: 'flex items-center gap-2 flex-shrink-0',
        reviewedPill:
            'flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-100 rounded-lg px-3 py-1.5',
        markUnread:
            'flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
    },
    panel: {
        markRead:
            'flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        markReadIcon: 'w-4 h-4',
        markReadSpinner:
            'h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin',
        reviewedGroup: 'flex items-center gap-2',
        reviewedPill:
            'flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2',
        markUnread:
            'flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 hover:bg-orange-100 transition-colors disabled:opacity-50'
    }
};

/** Mark as read / "Reviewed by …" + mark as unread pair. */
export const ConversationReadState: React.FC<ConversationActionsProps> = ({ actions, variant }) => {
    const {
        isUnread,
        reviewedBy,
        isMarkingRead,
        isMarkingUnread,
        handleMarkAsDone,
        handleMarkAsUnread
    } = actions;
    const styles = READ_STATE_STYLES[variant];

    if (isUnread) {
        return (
            <button onClick={handleMarkAsDone} disabled={isMarkingRead} className={styles.markRead}>
                {isMarkingRead ? (
                    <div className={styles.markReadSpinner} />
                ) : (
                    <CheckCircleIcon className={styles.markReadIcon} />
                )}
                Mark as read
            </button>
        );
    }

    return (
        <div className={styles.reviewedGroup}>
            <span className={styles.reviewedPill}>
                <CheckCircleIcon className="w-3.5 h-3.5" />
                {reviewedBy ? `Reviewed by ${reviewedBy}` : 'Reviewed'}
            </span>
            <button
                onClick={handleMarkAsUnread}
                disabled={isMarkingUnread}
                className={styles.markUnread}
            >
                {isMarkingUnread ? (
                    <div className="h-3.5 w-3.5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                    'Mark as unread'
                )}
            </button>
        </div>
    );
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">
        {children}
    </span>
);

/** Team + assignee selectors wired to the shared conversation handlers. */
export const ConversationAssignment: React.FC<ConversationActionsProps> = ({
    actions,
    variant
}) => {
    const {
        currentTeam,
        currentTeamRoleId,
        currentAssignee,
        currentUserEmail,
        handleTeamChange,
        handleAssign,
        handleUnassign
    } = actions;
    const teamRoles = useRoleNamesForId(currentTeamRoleId);

    const team = (
        <TeamSelector
            team={currentTeam}
            onSelect={handleTeamChange}
            size="sm"
            dropdownAlign="left"
        />
    );
    const assignee = (
        <AssigneeSelector
            assignedTo={currentAssignee}
            roles={teamRoles}
            onAssign={handleAssign}
            onUnassign={handleUnassign}
            currentUserEmail={currentUserEmail}
            dropdownAlign="left"
        />
    );

    if (variant === 'bar') {
        return (
            <div className="flex items-center gap-2 min-w-0">
                {team}
                {assignee}
            </div>
        );
    }

    return (
        <div className="flex items-end gap-5 min-w-0">
            <div className="flex flex-col items-start">
                <FieldLabel>Team</FieldLabel>
                {team}
            </div>
            <div className="flex flex-col items-start min-w-0">
                <FieldLabel>Assigned to</FieldLabel>
                {assignee}
            </div>
        </div>
    );
};
